import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import { AlertCircle, FileUp, Upload, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { EmptyState } from "components/states";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { FileInput } from "components/ui/file-input";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { db } from "integrations/firebase/firestore";
import { storage } from "integrations/firebase/storage";

import { AdminCommandFeedback } from "../AdminCommandFeedback";
import { useAdminCommand } from "../use-admin-command";

import {
  ATTENDEE_COLUMNS,
  attendeeMappingProblem,
  guessAttendeeMapping,
  type AttendeeMapping,
} from "./attendee-import-columns";
import {
  decodeImportCsv,
  MAX_IMPORT_BYTES,
  parseImportHeaders,
  readFileBytes,
  TOO_LARGE_CSV,
  UNDECODABLE_CSV,
} from "./import-csv";

/** The worker's `errorType` for a row whose person withdrew themselves. No
 * correction to the row changes that, so the row offers Dismiss and no Fix:
 * Restore on the Attendees tab is the way back. Copied from
 * functions/python/app/application/service/withdrawn_reimport.py. */
const WITHDRAWN_ERROR_TYPE = "withdrawn";

/** Copied from firestore.rules, which caps the DISMISS_IMPORT_ISSUES list. */
const MAX_DISMISSALS = 100;

type ImportStatus = "REQUESTED" | "IN_PROGRESS" | "SUCCEEDED" | "FAILED";

interface ImportProgress {
  status: ImportStatus;
  importedCount?: number;
  issueCount?: number;
  error?: string;
}

/**
 * One dismissal as the organiser asked for it, which may take several commands.
 *
 * `MAX_DISMISSALS` bounds the read set of a single transaction, which is the
 * right place for it. The list it gets applied to has no such bound: it is
 * every pending row of the event, so a 300-row import with 120 failures puts
 * 120 ids behind one button. Splitting is therefore the UI's job, and this
 * carries the split across the several commands it takes. It outlives any one
 * `dismiss.state`, so a failure part-way can still say how far the run got.
 */
interface DismissRun {
  /** Batches not yet submitted, in order. */
  pending: string[][];
  /** The batch whose command is being watched; empty once the run has stopped. */
  inFlight: string[];
  /** Ids earlier batches of this run have already dismissed. */
  done: number;
  /** Ids the run set out to dismiss. */
  total: number;
}

interface ImportIssue {
  id: string;
  errorMessage?: string;
  errorType?: string;
  rowNumber?: number;
  email?: string;
  commandId?: string;
}

/**
 * CSV attendee import.
 *
 * The ordering is forced by the contract and is easy to get wrong: the command
 * id has to be minted first because the rules pin `sourcePath` to
 * `events/<eventId>/imports/<commandId>.csv`, the file has to be in Storage
 * before the command document exists, and only then may the command be
 * written. The work then goes to Cloud Tasks, so the only honest progress
 * indicator is the command document itself.
 */
export function ImportSection({
  eventId,
  uid,
}: {
  eventId: string;
  uid: string;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<AttendeeMapping>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [commandId, setCommandId] = useState<string | null>(null);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [issues, setIssues] = useState<ImportIssue[]>([]);
  const [fixing, setFixing] = useState<ImportIssue | null>(null);
  const dismiss = useAdminCommand();
  const [dismissRun, setDismissRun] = useState<DismissRun | null>(null);

  const dismissBusy =
    dismiss.state.status === "submitting" ||
    dismiss.state.status === "watching" ||
    (dismissRun !== null && dismissRun.inFlight.length > 0);

  // A multi-batch run tells its own story; the shared strip is the wrong shape
  // for it, because it would flash "Dismissed" between every batch.
  const chunking = dismissRun !== null && dismissRun.total > MAX_DISMISSALS;

  /**
   * Advance the run: the batch that just landed leaves the list, and the next
   * batch goes to the same hook.
   *
   * Sequential rather than parallel. `useAdminCommand` holds one command's
   * state and one watcher, and a second `submit` drops the first — so
   * concurrent batches would report the last one's outcome for all of them.
   */
  useEffect(() => {
    if (dismissRun === null || dismissRun.inFlight.length === 0) return;
    if (dismiss.state.status === "succeeded") {
      const gone = new Set(dismissRun.inFlight);
      setIssues((current) => current.filter((issue) => !gone.has(issue.id)));
      const done = dismissRun.done + dismissRun.inFlight.length;
      const [next, ...rest] = dismissRun.pending;
      if (next === undefined) {
        setDismissRun(null);
        return;
      }
      setDismissRun({
        pending: rest,
        inFlight: next,
        done,
        total: dismissRun.total,
      });
      void dismiss.submit("DISMISS_IMPORT_ISSUES", eventId, uid, {
        issueIds: next,
      });
    } else if (dismiss.state.status === "failed") {
      // Stop. Each command is all-or-nothing in itself but the run is not, so
      // batches that already landed stay dismissed and everything from this one
      // on is still listed — which is what a reload would show anyway.
      setDismissRun({ ...dismissRun, pending: [], inFlight: [] });
    }
  }, [dismiss.state.status, dismiss.submit, dismissRun, eventId, uid]);

  function dismissIssues(ids: string[]) {
    if (ids.length === 0 || dismissBusy) return;
    const batches: string[][] = [];
    for (let at = 0; at < ids.length; at += MAX_DISMISSALS) {
      batches.push(ids.slice(at, at + MAX_DISMISSALS));
    }
    const [first, ...rest] = batches;
    if (first === undefined) return;
    setDismissRun({
      pending: rest,
      inFlight: first,
      done: 0,
      total: ids.length,
    });
    void dismiss.submit("DISMISS_IMPORT_ISSUES", eventId, uid, {
      issueIds: first,
    });
  }

  function resetDismissal() {
    setDismissRun(null);
    dismiss.reset();
  }

  async function chooseFile(chosen: File | null) {
    // Set once the file is known to be readable, so a refused file leaves
    // Start import disabled.
    setFile(null);
    setHeaders([]);
    setMapping({});
    setProblem(null);
    if (!chosen) return;
    // Storage refuses it anyway, with a permission error that reads like a
    // broken screen.
    if (chosen.size > MAX_IMPORT_BYTES) {
      setProblem(TOO_LARGE_CSV);
      return;
    }
    // The whole file, not the first bytes: a Windows-1252 export usually has
    // an ASCII header row, and its first umlaut can be anywhere below it. The
    // backend refuses such a file anyway, so say so before the upload.
    const text = decodeImportCsv(await readFileBytes(chosen));
    if (text === null) {
      setProblem(UNDECODABLE_CSV);
      return;
    }
    const parsed = parseImportHeaders(text);
    setFile(chosen);
    setHeaders(parsed);
    // Offer an obvious mapping when the headers already match.
    setMapping(guessAttendeeMapping(parsed));
  }

  /**
   * Every row of this event still waiting to be corrected, not the rows of the
   * run in front of us.
   *
   * Filtering on the in-memory `commandId` — which is what this did — meant a
   * reload lost the list entirely: nothing persists the command id, and the tab
   * itself resets to Settings, so an organiser who refreshed after importing
   * 300 rows with 12 failures had no route back to the 12. `status` is the
   * durable equivalent and the backend already owns it: the worker writes
   * "pending" (firestore_attendee_import_worker_repository.py) and a successful
   * RESOLVE_IMPORT_ISSUE flips it to "resolved", which the resolution
   * repository also requires, so a resolved row can never be fixed twice.
   *
   * Filtering on it therefore fixes a second bug at the same time: the old
   * query had no status clause, so a row you had just corrected stayed in the
   * list, and clicking Fix on it again submitted a command the backend refused
   * with "Import issue is missing or no longer pending".
   *
   * Equality on one field only, so this needs no composite index.
   */
  const loadIssues = useCallback(async () => {
    const snap = await getDocs(
      query(
        collection(db, "events", eventId, "import_issues"),
        where("status", "==", "pending"),
      ),
    );
    setIssues(
      snap.docs
        .map((d) => ({
          id: d.id,
          errorMessage: d.get("errorMessage") as string | undefined,
          errorType: d.get("errorType") as string | undefined,
          rowNumber: d.get("rowNumber") as number | undefined,
          email: d.get("email") as string | undefined,
          commandId: d.get("commandId") as string | undefined,
        }))
        // Ordered here rather than in the query, which would cost an index.
        // By run first, then row: the list now spans every unresolved import,
        // so row number alone would interleave two runs' rows meaninglessly.
        .sort(
          (a, b) =>
            (a.commandId ?? "").localeCompare(b.commandId ?? "") ||
            (a.rowNumber ?? 0) - (b.rowNumber ?? 0),
        ),
    );
  }, [eventId]);

  // On mount, because unresolved rows outlive the run that produced them.
  useEffect(() => {
    loadIssues().catch(() =>
      setProblem("Could not load the rows that still need attention."),
    );
  }, [loadIssues]);

  // The worker is out of process, so the command document is the only truth.
  useEffect(() => {
    if (!commandId) return;
    const unsub = onSnapshot(
      doc(db, "events", eventId, "admin_commands", commandId),
      (snap) => {
        if (!snap.exists()) return;
        const status = snap.get("status") as ImportStatus;
        setProgress({
          status,
          importedCount: snap.get("importedCount") as number | undefined,
          issueCount: snap.get("issueCount") as number | undefined,
          error: snap.get("error") as string | undefined,
        });
        if (status === "SUCCEEDED" || status === "FAILED") {
          void loadIssues();
        }
      },
    );
    return unsub;
  }, [commandId, eventId, loadIssues]);

  async function start() {
    if (!file) {
      setProblem("Choose a CSV file.");
      return;
    }
    const missing = attendeeMappingProblem(mapping);
    if (missing) {
      setProblem(missing);
      return;
    }
    setProblem(null);
    setBusy(true);
    setProgress(null);
    try {
      // Mint the id first — sourcePath is pinned to it.
      const commandRef = doc(
        collection(db, "events", eventId, "admin_commands"),
      );
      const sourcePath = `events/${eventId}/imports/${commandRef.id}.csv`;
      await uploadBytes(ref(storage, sourcePath), file, {
        contentType: "text/csv",
      });
      const columnMapping = Object.fromEntries(
        Object.entries(mapping).filter(([, header]) => Boolean(header)),
      );
      await setDoc(commandRef, {
        type: "IMPORT_ATTENDEES",
        requestedBy: uid,
        requestedAt: serverTimestamp(),
        eventId,
        status: "REQUESTED",
        payload: { sourcePath, columnMapping },
      });
      setCommandId(commandRef.id);
    } catch (err) {
      setProblem(
        err instanceof Error ? err.message : "Import failed to start.",
      );
    } finally {
      setBusy(false);
    }
  }

  const running =
    progress?.status === "REQUESTED" || progress?.status === "IN_PROGRESS";

  return (
    <div className="space-y-4" data-testid="import-content">
      <Card className="border-border shadow-none">
        <CardContent className="space-y-4 pt-6">
          <div className="space-y-1.5">
            <Label htmlFor="import-file">CSV file *</Label>
            <FileInput
              id="import-file"
              ref={fileInput}
              accept=".csv,text/csv"
              testId="import-file"
              disabled={busy || running}
              onChange={(e) => void chooseFile(e.target.files?.[0] ?? null)}
            />
            <p className="text-xs text-muted-foreground">
              Up to 10 MB, saved as CSV UTF-8 with commas or semicolons between
              columns. The first row must be a header row. First name and Last
              name are required; Email, Job title, Company, Track and Attendee
              type (internal or external; intern and extern work too) are
              optional. Columns named like these, in English or German (Vorname,
              Thementisch, Teilnehmertyp), are mapped automatically.
            </p>
            <p className="text-xs text-muted-foreground">
              An email is recommended — it links the row to the person when they
              register. A row with no email becomes a directory-only listing:
              visible on the attendee list, never linked to a registration.
            </p>
          </div>

          {headers.length > 0 && (
            <div className="space-y-3" data-testid="import-mapping">
              <p className="text-sm font-medium text-foreground">
                Match your columns
              </p>
              {ATTENDEE_COLUMNS.map(({ key, label, required }) => (
                <div
                  key={key}
                  className="flex items-center justify-between gap-3"
                >
                  <Label htmlFor={`map-${key}`} className="shrink-0">
                    {label}
                    {required ? " *" : ""}
                  </Label>
                  <select
                    id={`map-${key}`}
                    data-testid={`map-${key}`}
                    value={mapping[key] ?? ""}
                    disabled={busy || running}
                    onChange={(e) =>
                      setMapping({ ...mapping, [key]: e.target.value })
                    }
                    className="h-9 w-full min-w-0 rounded-xl border border-input bg-background px-2 text-base text-foreground max-sm:h-11 sm:w-56 sm:text-sm"
                  >
                    <option value="">Not imported</option>
                    {headers.map((header) => (
                      <option key={header} value={header}>
                        {header}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}

          {problem && (
            <p
              className="text-sm text-destructive-text"
              data-testid="import-error"
            >
              {problem}
            </p>
          )}

          <Button
            variant="cta"
            onClick={start}
            disabled={busy || running || !file}
            data-testid="start-import"
          >
            <Upload className="h-4 w-4" />
            {running ? "Importing…" : "Start import"}
          </Button>
        </CardContent>
      </Card>

      {progress && (
        <Card className="border-border shadow-none">
          <CardContent className="space-y-2 py-4" data-testid="import-progress">
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                data-testid={`import-status-${progress.status}`}
              >
                {progress.status}
              </Badge>
              {progress.importedCount != null && (
                <span className="text-sm text-muted-foreground">
                  {progress.importedCount} imported
                </span>
              )}
              {progress.issueCount != null && progress.issueCount > 0 && (
                <span className="text-sm text-muted-foreground">
                  {progress.issueCount} need attention
                </span>
              )}
            </div>
            {progress.error && (
              <p className="text-sm text-destructive-text">{progress.error}</p>
            )}
          </CardContent>
        </Card>
      )}

      {issues.length > 0 && (
        <div className="space-y-2" data-testid="import-issues">
          <p className="text-sm font-medium text-foreground">
            Rows that still need attention
          </p>
          {/* Below the heading rather than beside it, so this stays a pure
              insertion: feat/attendee-import-job rewords that line, and
              wrapping it would turn a clean merge into a conflict. */}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={dismissBusy}
              data-testid="dismiss-all-issues"
              onClick={() => dismissIssues(issues.map((issue) => issue.id))}
            >
              Dismiss all {issues.length}
            </Button>
            {issues.length > MAX_DISMISSALS && (
              <span className="text-xs text-muted-foreground">
                Sent as {Math.ceil(issues.length / MAX_DISMISSALS)} commands of
                up to {MAX_DISMISSALS} rows, so a failure part-way through
                leaves the rest listed.
              </span>
            )}
          </div>
          {chunking && dismissRun.inFlight.length > 0 ? (
            <p
              className="text-sm text-muted-foreground"
              data-testid="dismiss-progress"
            >
              Dismissing {dismissRun.total} rows. {dismissRun.done} done.
            </p>
          ) : (
            <AdminCommandFeedback
              state={dismiss.state}
              onReset={resetDismissal}
              // Not "dismiss-issue": that is the prefix of the per-row buttons,
              // so a row whose id were "failed" would collide with the strip.
              testId="dismiss-command"
            />
          )}
          {dismissRun !== null &&
            dismissRun.inFlight.length === 0 &&
            dismissRun.done > 0 && (
              <p
                className="text-xs text-muted-foreground"
                data-testid="dismiss-partial"
              >
                {dismissRun.done} of {dismissRun.total} rows were dismissed
                before that failure. The rest are still listed.
              </p>
            )}
          {issues.map((issue) => (
            <Card key={issue.id} className="border-border shadow-none">
              <CardContent className="flex items-center justify-between gap-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm text-foreground">
                    {issue.rowNumber != null ? `Row ${issue.rowNumber}: ` : ""}
                    {issue.email ?? "(no email)"}
                  </p>
                  <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive-text" />
                    {issue.errorMessage ?? issue.errorType ?? "Unknown problem"}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  {issue.errorType !== WITHDRAWN_ERROR_TYPE && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={dismissBusy}
                      data-testid={`fix-issue-${issue.id}`}
                      onClick={() => setFixing(issue)}
                    >
                      Fix
                    </Button>
                  )}
                  {/* The list's only other exit. Until this existed a row an
                      organiser had decided not to import stayed in it for
                      ever, because resolving demands corrections that make the
                      row import. */}
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={dismissBusy}
                    aria-label={`Dismiss row ${issue.rowNumber ?? issue.id}`}
                    data-testid={`dismiss-issue-${issue.id}`}
                    onClick={() => dismissIssues([issue.id])}
                  >
                    <X className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <FixIssueDialog
        // Remounts per row, which is what seeds the form from that row rather
        // than needing an effect to re-seed a reused dialog.
        key={fixing?.id ?? "none"}
        eventId={eventId}
        uid={uid}
        issue={fixing}
        onClose={() => setFixing(null)}
        onResolved={() => {
          setFixing(null);
          void loadIssues();
        }}
      />

      {!progress && !file && issues.length === 0 && (
        <EmptyState icon={FileUp} title="Import attendees">
          Upload a CSV to add attendees in bulk. Rows that cannot be imported
          are listed afterwards so you can correct them.
        </EmptyState>
      )}
    </div>
  );
}

// The seven fields RESOLVE_IMPORT_ISSUE accepts, in the order a person reads a
// row. Both the rules and the service pin this set exactly.
const CORRECTION_FIELDS: {
  name: string;
  label: string;
  options?: string[];
}[] = [
  { name: "firstName", label: "First name" },
  { name: "lastName", label: "Last name" },
  { name: "email", label: "Email" },
  { name: "jobTitle", label: "Job title" },
  { name: "company", label: "Company" },
  { name: "trackName", label: "Track" },
  // The worker accepts only these two; a free-text box here would send the row
  // straight back to the issue list.
  {
    name: "attendeeType",
    label: "Attendee type",
    options: ["internal", "external"],
  },
];

/**
 * Correct a row the import could not take, and re-run just that row.
 *
 * Until this existed the issue list was a dead end: RESOLVE_IMPORT_ISSUE had
 * shipped and was rule-permitted, but nothing filed it, so a failed row could
 * never be recovered — the only way back was to fix the CSV and import the
 * whole file again.
 *
 * Only fields the admin actually fills are sent. The rules require between one
 * and seven corrections, so an empty submission is refused at the door; the
 * button is disabled for the same case, since a permission-denied here reads
 * like a broken screen rather than an empty form.
 *
 * Exported for its own test: reaching it through ImportSection means driving a
 * CSV upload and a Cloud Tasks round trip first, and the contract worth pinning
 * is small and local — which corrections leave the form.
 */
export function FixIssueDialog({
  eventId,
  uid,
  issue,
  onClose,
  onResolved,
}: {
  eventId: string;
  uid: string;
  issue: ImportIssue | null;
  onClose: () => void;
  onResolved: () => void;
}) {
  const command = useAdminCommand();
  // The only value the issue document carries that maps to a correction field.
  const [values, setValues] = useState<Record<string, string>>(() =>
    issue?.email ? { email: issue.email } : ({} as Record<string, string>),
  );

  useEffect(() => {
    if (command.state.status === "succeeded") onResolved();
  }, [command.state.status, onResolved]);

  const corrections = Object.fromEntries(
    Object.entries(values).filter(([, value]) => value.trim() !== ""),
  );
  const busy =
    command.state.status === "submitting" ||
    command.state.status === "watching";

  return (
    <Dialog open={issue !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Fix row {issue?.rowNumber ?? ""}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          {issue?.errorMessage ?? issue?.errorType ?? "This row was rejected."}
        </p>

        <div className="space-y-3">
          {CORRECTION_FIELDS.map((field) => (
            <div key={field.name} className="space-y-1.5">
              <Label htmlFor={`fix-${field.name}`}>{field.label}</Label>
              {field.options ? (
                <select
                  id={`fix-${field.name}`}
                  data-testid={`fix-${field.name}`}
                  className="w-full rounded-xl border border-input bg-background px-3 py-2 text-base text-foreground max-sm:min-h-11 sm:text-sm"
                  value={values[field.name] ?? ""}
                  onChange={(e) =>
                    setValues({ ...values, [field.name]: e.target.value })
                  }
                >
                  <option value="">Leave unchanged</option>
                  {field.options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : (
                <Input
                  id={`fix-${field.name}`}
                  testId={`fix-${field.name}`}
                  value={values[field.name] ?? ""}
                  onChange={(e) =>
                    setValues({ ...values, [field.name]: e.target.value })
                  }
                />
              )}
            </div>
          ))}
        </div>

        <AdminCommandFeedback
          state={command.state}
          onReset={command.reset}
          testId="fix-issue"
        />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="cta"
            disabled={
              busy || issue === null || Object.keys(corrections).length === 0
            }
            data-testid="submit-fix-issue"
            onClick={() =>
              issue &&
              void command.submit("RESOLVE_IMPORT_ISSUE", eventId, uid, {
                issueId: issue.id,
                corrections,
              })
            }
          >
            {busy ? "Retrying…" : "Retry this row"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
